/**
 * Custom hook for hold batch operations (confirm, challenge, release)
 */

import type { HoldList } from 'api-client/models';
import { useAppContext } from '@/context/AppContext';
import { readHoldBatchResult } from '../utils/holdBatchResults';
import { isHoldActionable } from '../utils/holdSelection';

interface UseHoldOperationsResult {
  confirmOrChallengeHolds: (holdIds: string[], holds: HoldList[]) => Promise<void>;
  releaseHoldsBatch: (
    holdIds: Set<string>,
    holds: HoldList[],
  ) => Promise<void>;
  respondToChallenge: (
    holdId: string,
    response: 'confirm' | 'release',
  ) => Promise<void>;
}

/**
 * Hook to manage batch hold operations
 * Includes confirm, challenge, release, and challenge response actions
 */
export const useHoldOperations = (
  setInfoMessage: (message: string | null) => void,
  setError: (error: string | null) => void,
  refetch: () => Promise<void>,
): UseHoldOperationsResult => {
  const { apiClients } = useAppContext();

  const confirmOrChallengeHolds = async (holdIds: string[], holds: HoldList[]): Promise<void> => {
    setError(null);
    setInfoMessage(null);
    const selectedHolds = holds.filter((h) => holdIds.includes(h.id) && h.status === 'pending');
    if (holdIds.length === 0 || new Set(holdIds).size !== holdIds.length
      || selectedHolds.length !== holdIds.length
      || selectedHolds.some((hold) => !hold.holdPosition || hold.holdPosition < 1)) {
      const message = 'Select current pending dates with a valid hold position.';
      setError(message);
      throw new Error(message);
    }
    const firstPositionHoldIds = selectedHolds.filter((h) => h.holdPosition === 1).map((h) => h.id);
    const higherPositionHoldIds = selectedHolds.filter((h) => (h.holdPosition || 0) > 1).map((h) => h.id);
    const failures: string[] = [];
    const messageParts: string[] = [];

    if (firstPositionHoldIds.length > 0) {
      try {
        const response = await apiClients.holds.holdsConfirmBatchRaw({
          holdsConfirmBatchRequest: { holdIds: firstPositionHoldIds },
        });
        const value: unknown = await response.raw.json();
        const result = readHoldBatchResult(value, 'confirmed_count', firstPositionHoldIds);
        failures.push(...result.errors);
        if (result.count > 0) {
          messageParts.push(`${result.count} date${result.count > 1 ? 's' : ''} confirmed`);
        }
      } catch (error) {
        failures.push(error instanceof Error ? error.message : 'Failed to confirm selected dates.');
      }
    }
    if (higherPositionHoldIds.length > 0) {
      try {
        const response = await apiClients.holds.holdsChallengeBatchRaw({
          holdsChallengeBatchRequest: { holdIds: higherPositionHoldIds, responseHours: 24 },
        });
        const value: unknown = await response.raw.json();
        const result = readHoldBatchResult(value, 'challenged_count', higherPositionHoldIds);
        failures.push(...result.errors);
        if (result.count > 0) {
          messageParts.push(`${result.count} challenge${result.count > 1 ? 's' : ''} initiated`);
        }
      } catch (error) {
        failures.push(error instanceof Error ? error.message : 'Failed to challenge selected dates.');
      }
    }
    if (messageParts.length > 0) setInfoMessage(`${messageParts.join('. ')}.`);
    try {
      await refetch();
    } catch {
      failures.push('Failed to refresh dates. Reload to verify their current state.');
    }
    if (failures.length > 0) {
      const message = failures.join(' ');
      setError(message);
      throw new Error(message);
    }
  };

  /**
   * Release holds in batch
   */
  const releaseHoldsBatch = async (
    holdIds: Set<string>,
    holds: HoldList[],
  ) => {
    setError(null);
    setInfoMessage(null);
    const selectedHolds = holds.filter((hold) => holdIds.has(hold.id));
    if (holdIds.size === 0 || selectedHolds.length !== holdIds.size || selectedHolds.some((hold) => !isHoldActionable(hold))) {
      setError('Select current pending or challenged dates to release.');
      return;
    }
    const outcomes = await Promise.all(selectedHolds.map(async (hold) => {
      try {
        const fullHold = await apiClients.holds.holdsRetrieve({ id: hold.id });
        if (fullHold.id !== hold.id || fullHold.show !== hold.show || !isHoldActionable(fullHold)) {
          throw new Error('This date is no longer eligible for release. Refresh and inspect its current state.');
        }
        await apiClients.holds.holdsRelease({ id: hold.id, hold: fullHold });
        return { released: true, error: null };
      } catch (err) {
        return { released: false, error: `${hold.id}: ${err instanceof Error ? err.message : 'Failed to release hold'}` };
      }
    }));
    const released = outcomes.filter((result) => result.released).length;
    const failures = outcomes.flatMap((result) => result.error ? [result.error] : []);
    if (released > 0) setInfoMessage(`Released ${released} hold${released > 1 ? 's' : ''}`);
    try {
      await refetch();
    } catch {
      failures.push('Failed to refresh dates. Reload to verify their current state.');
    }
    if (failures.length > 0) setError(failures.join(' '));
  };

  /**
   * Respond to a challenged hold
   */
  const respondToChallenge = async (
    holdId: string,
    response: 'confirm' | 'release',
  ) => {
    setError(null);
    setInfoMessage(null);
    try {
      await apiClients.holds.holdsRespondToChallenge({
        id: holdId,
        holdsRespondToChallengeRequest: {
          response,
        },
      });

      if (response === 'confirm') {
        setInfoMessage('Hold confirmed successfully! Challenge rejected.');
      } else {
        setInfoMessage('Hold released. Challenge cascade continues.');
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${response} hold`;
      setError(message);
      throw new Error(message);
    }
    try {
      await refetch();
    } catch {
      const message = 'Failed to refresh dates. Reload to verify their current state before responding again.';
      setError(message);
      throw new Error(message);
    }
  };

  return {
    confirmOrChallengeHolds,
    releaseHoldsBatch,
    respondToChallenge,
  };
};
